import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useSnackbar } from 'notistack';

export interface WebSocketMessage {
  type: 'connection' | 'status_update' | 'progress_update' | 'ping' | 'pong' | 'error';
  status?: string;
  filename?: string;
  message?: string;
  progress?: number;
  stage?: string;
  timestamp: string;
}

interface WebSocketContextType {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  sendMessage: (message: any) => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { isAuthenticated, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const pingInterval = useRef<NodeJS.Timeout | null>(null);
  
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  
  const connect = () => {
    if (!isAuthenticated || !user) {
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsBaseUrl = apiUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsBaseUrl}/ws/${encodeURIComponent(user.email)}`;
    
    try {
      setConnectionStatus('connecting');
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        
        // Start ping interval to keep connection alive
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.current.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          setLastMessage(message);
          
          // Handle different message types
          switch (message.type) {
            case 'status_update':
              if (message.status === 'completed') {
                enqueueSnackbar('Analysis complete! Check your email for results.', { 
                  variant: 'success',
                  autoHideDuration: 8000,
                });
              } else if (message.status === 'processing') {
                enqueueSnackbar('Your medical report is being analyzed...', { 
                  variant: 'info',
                  autoHideDuration: 5000,
                });
              } else if (message.status === 'failed') {
                enqueueSnackbar(message.message || 'Processing failed', { 
                  variant: 'error',
                  autoHideDuration: 8000,
                });
              } else if (message.status === 'uploaded') {
                enqueueSnackbar('File uploaded successfully! Processing will begin shortly.', { 
                  variant: 'success',
                  autoHideDuration: 5000,
                });
              }
              break;
            
            case 'progress_update':
              enqueueSnackbar(`Processing: ${message.stage} (${message.progress}%)`, { 
                variant: 'info',
                autoHideDuration: 3000,
              });
              break;
            
            case 'connection':
              console.log('WebSocket connection established');
              break;
            
            case 'pong':
              // Response to ping, connection is alive
              break;
            
            case 'error':
              enqueueSnackbar(message.message || 'WebSocket error', { 
                variant: 'error',
                autoHideDuration: 5000,
              });
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        // Clear ping interval
        if (pingInterval.current) {
          clearInterval(pingInterval.current);
          pingInterval.current = null;
        }
        
        // Attempt to reconnect if not a normal closure
        if (event.code !== 1000 && isAuthenticated && user) {
          setConnectionStatus('connecting');
          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, 3000); // Reconnect after 3 seconds
        }
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        setIsConnected(false);
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('error');
    }
  };

  const disconnect = () => {
    // Clear timeouts and intervals
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }
    
    // Close WebSocket connection
    if (ws.current) {
      ws.current.close(1000, 'User logout');
      ws.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
  };

  const sendMessage = (message: any) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  };

  // Connect when authenticated, disconnect when not
  useEffect(() => {
    if (isAuthenticated && user) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  const value: WebSocketContextType = {
    isConnected,
    lastMessage,
    sendMessage,
    connectionStatus,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}










